<template>
  <div>
    <h3>
      <span>
        {{ $t('message.script_security_ip_filter') | title }}
      </span>
      <span class="wiki-link">
        <a :href="$t('message.script_security_ip_filter_doc_link')" target="_blank">
          WIKI
        </a>
      </span>
    </h3>
    <div v-if="willRunOnEachClusterNode" class="cluster-info">
      {{ $t('message.will_run_on_each_cluster_node') | title }}
    </div>
    <div v-if="willRunOnThisClusterNodeOnly" class="cluster-info">
      {{ $t('message.will_run_on_this_cluster_node_only') | title }}
    </div>

    <div class="description">{{$t("message.script_security_ip_filter_desc")}}</div>

    <div class='request-address'>
      <div v-if="requestAddress" >
        <span class="label label-defaul col-2">
          {{$t("message.request_address")}}:
        </span>
        <span>
          {{requestAddress}}
        </span>
      </div>
      <div v-if="requestRoute" class="label label-defaul">
        <span class="label label-defaul col-2">
          {{$t("message.request_route")}}:
        </span>
        <span v-for="route in requestRoute"> {{route}} </span>
      </div>
    </div>


    <b-form v-if="ipFilterConfig" id="ip-filter-form">
      <div id="enable-part">
        <b-form-row>
          <span class="label label-default col-2">
            {{$t("message.script_security_ip_filter_enabled") | title}}
          </span>
          <div class="col-1">
            <div class="form-group form-check">
              <input type="checkbox" class="form-check-input" v-model="ipFilterConfig.enabled"
                @input="dirty=true" @change="onEnabledChanged()"/>
            </div>
          </div>
        </b-form-row>
        <b-form-row>
          <span class="label label-default col-2">
            {{$t("message.script_security_ip_filter_confirm_time") | title}}
          </span>
          <b-form-select v-model="ipFilterConfig.confirm_time" class="col-1" @input="dirty=true"
            :options="confirmTimeOptions"/>
        </b-form-row>
      </div>
      <b-form-row >
        <span class="label label-default col-2 text-center">
          {{$t('message.script_security_ip_filter_applies_to') | title}}
        </span>
        <span class="label label-default col-2 text-center">
          {{$t('message.script_security_ip_filter_name') | title}}
        </span>
        <span class="label label-default col-3 text-center">
          {{$t('message.script_security_ip_filter_src') | title}} / {{$t('message.network_netmask') | title}}
        </span>
        <span class="label label-default col-2 text-center">
          {{$t('message.script_security_ip_filter_verdict') | title}}
        </span>
      </b-form-row>
      <b-form-row v-if="ipFilterConfig.enabled" v-for="(rule, ruleIndex) in ipFilterConfig.rules">
        <b-form-select :options="appliesToOptions" class="col-2 text-center form-control" v-model="rule.applies_to" @input="dirty=true"/>
        <b-form-input type="text" class="col-2 text-center" v-model="rule.name" @input="dirty=true"/>
        <span class="col-3 text-center">
          <VueIpInput :ref="'vii' + ruleIndex" :key="componentKey" class="ip-input" :ip="rule.src"
            :on-change="getSrcChangeHandler(ruleIndex)" @input="dirty=true"/>
          /
          <input type="number" v-model="rule.netmask" class="netmask-input text-center" min="0" max="32" @input="dirty=true"/>
        </span>
        <span class="col-2">
          <b-form-select v-model="rule.verdict" :options="verdictOptions" class="form-control text-center" @input="dirty=true"/>
        </span>
        <span class="offset-1">
          <svg class='icon icon-delete' @click="delRule(ruleIndex)" :disabled="inProgress">
            <title>{{$t("message.button_delete")}}</title>
          </svg>
          <svg class='icon icon-up-arrow' v-if="ipFilterConfig.rules.length && ruleIndex != 0"
            @click="moveRuleUp(ruleIndex)" :disabled="inProgress">
            <title>{{$t("message.button_move_up")}}</title>
          </svg>
          <svg class='icon icon-down-arrow' v-if="ipFilterConfig.rules.length > 1 && ruleIndex != ipFilterConfig.rules.length-1"
            @click="moveRuleDown(ruleIndex)" :disabled="inProgress">
            <title>{{$t("message.button_move_down")}}</title>
          </svg>
        </span>
      </b-form-row>
      <b-form-row>
        <b-form-select :options="appliesToOptions" class="col-2 text-center form-control" v-model="endingRule.applies_to" disabled/>
        <b-form-input type="text" class="col-2 text-center" v-model="endingRule.name" disabled/>
        <span class="col-3 text-center">
          <b-form-input type="text" class="col-8 inline-block" disabled/>/
          <input type="number" class="netmask-input" disabled/>
        </span>
        <span class="col-2">
          <b-form-select v-model="endingRule.verdict" :options="verdictOptions" class="form-control text-center" disabled/>
        </span>
      </b-form-row>
      <div>
        <b-button class="padmin-button" @click="addRule" variant="primary"
          :disabled="inProgress || !ipFilterConfig.enabled">
          {{$t('message.button_new') | title}}
        </b-button>
        <b-button class="padmin-button submit-button" @click="save"
          :disabled="inProgress || !dirty" variant="primary">
          <b-spinner v-if="inProgress" small type="grow"/>
          {{$t('message.button_save') | title}}
        </b-button>
      </div>
    </b-form>

    <b-alert v-model="showError" variant="danger" dismissible @dismissed="error=''; showError=false;">
      <div> {{ error }} </div>
    </b-alert>

    <b-modal ref="confirm-ip-filter-modal" id="confirm-ip-filter-modal-id"
      :title="$t('message.script_security_ip_filter_confirm_title')" no-close-on-backdrop>
      <div>{{$t("message.script_security_ip_filter_confirm_desc")}}</div>

      <div id="ip-filter-state">
        {{$t("message.script_security_ip_filter_state_" + confirmState)}}
        <span v-if="confirmState != 'confirmed' && confirmState != 'reverted'">
          {{confirmTimeLeft}}
        </span>
      </div>

      <template #modal-footer>
        <div>
          <b-button class="padmin-button submit-button float-right" @click="confirm"
            :disabled="confirmState=='confirmed' || confirmState=='reverted'">
            {{$t("message.button_confirm") | title}}
          </b-button>
        </div>
      </template>
    </b-modal>

    <ConsoleOutput v-if="result != null" :result="result" :output="output"/>
  </div>
</template>

<script>
import VueIpInput from "vue-ip-input";
import { get, post } from "../http";
import ConsoleOutput from "./ConsoleOutput";
import { validateAlphanumericWithExtras, validateIpAddress, validateIpAddressesOnSameSubnet, validateHostName } from "../util";

export default {
  name: "IpFilterConfig",

  components: {
    ConsoleOutput, VueIpInput
  },

  data() {
    return {
      ip: '127.0.0.1',
      ipFilterConfig: null,
      formError: '',
      inProgress: false,
      result: null,
      output: '',
      error: '',
      verdictOptions: ["allow", "deny"],
      dirty: false,
      endingRule: { applies_to: 'Padmin,PassMan', name: 'end', verdict: 'deny'},
      requestAddress: null,
      requestRoute: null,
      confirmTimeLeft: 0,
      confirmState: 'not_confirmed',
      confirmTimeOptions: [{value: 10, text: '10s'}, {value: 30, text: '30s'}, {value: 60, text: '60s'}],
      appliesToOptions: ['Padmin', 'PassMan', 'Padmin,PassMan'],
      showError: false
    }
  },

  mounted() {
    this.loadRequestAddress()
    this.load()
  },

  methods: {
    getUrl() {
      return '/scripts/security/ip_filter'
    },

    onEnabledChanged() {
      this.endingRule.verdict = this.ipFilterConfig.enabled ? 'deny': 'allow'
    },

    loadRequestAddress() {
      get('/request_address').then(
        resp => {
          this.requestAddress = resp.data.address
          this.requestRoute = resp.data.route
        },
        err => {
          console.log('err', err)
        }
      )
    },

    load() {
      this.inProgress = true
      this.output = ''
      this.error = ''
      this.formError = ''
      this.dirty = false
      get(this.getUrl()).then(
        resp => {
          this.inProgress = false
          this.willRunOnEachClusterNode = resp.data.will_run_on_each_cluster_node
          this.willRunOnThisClusterNodeOnly = resp.data.will_run_on_this_cluster_node_only

          this.ipFilterConfig = {
            enabled: resp.data['params']['enabled']['value'],
            rules: resp.data['params']['rules']['value'],
            confirmed: resp.data['params']['confirmed']['value'],
            confirm_time: resp.data['params']['confirm_time']['value']
          }

          if (this.ipFilterConfig.enabled == null) {
            this.ipFilterConfig.enabled = false
          }
          if (this.ipFilterConfig.rules == null) {
            this.ipFilterConfig.rules = []
          }
          if (this.ipFilterConfig.config_time == null) {
            this.ipFilterConfig.config_time = 10
          }
        },
        err => {
          this.inProgress = false
          console.log('err', err)
        }
      )
    },

    showConfirmModal() {
      this.confirmState = 'not_confirmed'
      this.confirmTimeLeft = this.ipFilterConfig.confirm_time
      this.$root.$emit('bv::show::modal', 'confirm-ip-filter-modal-id')
      var intThis = this
      let intervalId = setInterval(function () {
        intThis.confirmTimeLeft -= 1
        if (intThis.confirmState == 'confirmed') {
          clearInterval(intervalId)
          setTimeout(()=> {intThis.$root.$emit('bv::hide::modal', 'confirm-ip-filter-modal-id')}, 2000)
        } else if (intThis.confirmTimeLeft == 0) {
          intThis.confirmState = 'reverted'
          clearInterval(intervalId)
          setTimeout(()=> {intThis.load()}, 2000)
        }
      }, 1000)
    },

    confirm() {
      post('/confirm_ip_filter').then(
        resp => {
          if (resp.data.result) {
              this.confirmState = 'confirmed'
          } else {
            this.confirmState = 'error'
          }

        },
        err => {
          this.confirmState = 'confirm_failed'
          console.log('err', err)
        }
      )
    },

    save() {
      this.result = null
      this.output = ''
      this.showError = false
      this.error = ''
      this.dirty = false

      for(let ruleIndex=0; ruleIndex < this.ipFilterConfig.rules.length; ruleIndex++) {
        let rule = this.ipFilterConfig.rules[ruleIndex]
        if (rule.name && !validateAlphanumericWithExtras(rule.name)) {
          this.error = rule.name + ": " + this.$t("message.error_invalid_name")
          this.showError = true
          return
        }

        if (!validateIpAddress(rule.src)) {
          this.error = rule.src + ": " + this.$t("message.error_invalid_ip")
          this.showError = true
          return
        }
        if (rule.netmask < 0 || rule.netmask > 32) {
          this.error = rule.netmask + ":" + this.$t("message.error_invalid_netmask")
          this.showError = true
          return
        }

        if (rule.port < 0 || rule.port > 65535) {
          this.error = rule.port + ":" + this.$t("message.error_invalid_port")
          this.showError = true
          return
        }
      }

      this.ipFilterConfig.confirmed = false
      this.inProgress = true

      post(this.getUrl(), this.ipFilterConfig, {'Content-Type': 'application/json'}).then(
        resp => {
          this.result = resp.data.result
          this.output = resp.data.output
          this.inProgress = false
          if (resp.data.error) {
            this.error = resp.data.error
            this.showError = true
          }
          if(this.result) {
            this.showConfirmModal()
          }
        },
        err => {
          this.inProgress = false
          console.log('err', err)
        }
      )
    },

    addRule() {
      this.ipFilterConfig.rules.push({
        applies_to: 'Padmin,PassMan', name: '', src: '', netmask: '24', port: '80,443', verdict: 'allow'
      })
    },

    delRule(ruleIndex) {
      this.dirty = true
      this.ipFilterConfig.rules.splice(ruleIndex, 1)
    },

    moveRuleUp(ruleIndex) {
      this.switchNextRules(ruleIndex - 1)
    },

    moveRuleDown(ruleIndex) {
      this.switchNextRules(ruleIndex)
    },

    switchNextRules(ruleIndex1) {
      let ruleIndex2 = ruleIndex1 + 1
      let newRule1 = Object.assign({}, this.ipFilterConfig.rules[ruleIndex2])
      let newRule2 = Object.assign({}, this.ipFilterConfig.rules[ruleIndex1])
      this.ipFilterConfig.rules.splice(ruleIndex1, 2, newRule1, newRule2)

      //vue ip input does not handle if value is empty string - do it here
      if (newRule1.src == "") {
        this.$refs['vii' + ruleIndex1][0].segments = ['', '', '', '']
      }
      if (newRule2.src == "") {
        this.$refs['vii' + ruleIndex2][0].segments = ['', '', '', '']
      }
    },

    getSrcChangeHandler(ruleIndex) {
      let that = this
      return function (val) {
        let rule = that.ipFilterConfig.rules[ruleIndex]
        rule.src = val.replace(/\s+/g, '')
        that.$set(that.ipFilterConfig.rules, ruleIndex, rule)
        that.$set(that.ipFilterConfig, 'rules', that.ipFilterConfig.rules)
        that.dirty = true
      }
    },
  }
}
</script>
<style scoped>
  .padmin-button {
    margin-left: 10px;
  }
  .up-down-button {
    width: 50px !important;
  }
  #ip-filter-form {
    min-width: 1000px;
    max-width: 1000px;
  }
  #enable-part {
    margin-bottom: 50px;
  }
  #ip-filter-state {
    text-align: center;
    margin-top: 20px;
    margin-bottom: 20px;
  }
  .inline-block {
    display: inline-block;
  }
</style>
